import { useState } from "react";
import { Icon } from "./icons";

const GUIDE: { icon: string; title: string; body: string }[] = [
  { icon: "dashboard", title: "Dashboard", body: "Your morning glance: total money owed (open dues), active rentals, and fleet status. Skeletons show while data loads, then real numbers appear." },
  { icon: "clients", title: "Clients & Leads", body: "Add clients with “Add client.” Use “License / docs” to upload a driver’s license, insurance, or agreement. A client needs a valid, non-expired license on file before you can start a rental for them. Website enquiries land here as Leads." },
  { icon: "rentals", title: "Rentals", body: "“New rental” → pick a client and vehicle, set the dates and rate. Creating an active rental automatically flips that vehicle to RENTED. Edit a rental to change status — marking it COMPLETED/CANCELLED frees the vehicle back to AVAILABLE." },
  { icon: "fleet", title: "Fleet", body: "Your vehicles. Add or edit cars here. Setting a vehicle to IN_SHOP automatically opens a Shop visit so the two stay in sync." },
  { icon: "payments", title: "Payments", body: "Open and overdue payment dues across your rentals, soonest first." },
  { icon: "maintenance", title: "Maintenance", body: "Vehicles with service coming due (by date or mileage)." },
  { icon: "shop", title: "Shop tracker", body: "Vehicles currently at the shop. “Send to shop” marks a vehicle IN_SHOP; updating a visit to RETURNED frees the vehicle back to AVAILABLE." },
];

export function HelpButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} title="Help & guide"
        className="inline-flex items-center gap-2 rounded-xl border border-black/10 px-3.5 py-2 text-sm font-semibold hover:bg-black/[0.04]">
        <Icon name="help" size={16} /> Help
      </button>
      {open && <HelpModal onClose={() => setOpen(false)} />}
    </>
  );
}

function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[86vh] overflow-y-auto bg-paper-card rounded-2xl p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-2xl font-extrabold tracking-tight">Guide & tutorial</h2>
          <button onClick={onClose} className="text-ink-muted hover:text-ink text-lg leading-none">✕</button>
        </div>
        <p className="text-ink-muted mb-6">A quick tour of FleetLease and how notifications work.</p>

        {/* Notifications — highlighted */}
        <div className="rounded-2xl border border-accent/20 bg-accent-50 p-5 mb-6">
          <div className="flex items-center gap-2 mb-2"><Icon name="bell" size={18} className="text-accent" /><h3 className="font-bold">Notifications (Settings)</h3></div>
          <p className="text-sm text-ink/80 mb-3">Get alerted when something needs you — maintenance due, a payment received, a car ready for pickup, an overdue payment, and more. In <b>Settings</b>, set where alerts go and tick which events you want for each channel.</p>
          <div className="space-y-2.5">
            <div className="flex items-start gap-2.5 rounded-xl bg-white p-3">
              <Icon name="mail" size={18} className="text-ok mt-0.5" />
              <div className="text-sm"><b className="text-ok">Email — live now.</b> Add your notification email, toggle the events you care about, and hit Save. Use <b>“Send test email”</b> to confirm it’s working.</div>
            </div>
            <div className="flex items-start gap-2.5 rounded-xl bg-white p-3">
              <Icon name="phone" size={18} className="text-warn mt-0.5" />
              <div className="text-sm"><b className="text-warn">Text (SMS) — not ready yet.</b> Texting is pending carrier/AWS approval. You can choose your SMS preferences now, but <b>no texts will send until it’s approved</b>. Email works in the meantime — we’ll flip SMS on once it clears (usually a few business days).</div>
            </div>
          </div>
        </div>

        {/* Section tour */}
        <h3 className="font-bold mb-3">What’s on each screen</h3>
        <div className="space-y-3">
          {GUIDE.map((g) => (
            <div key={g.title} className="flex items-start gap-3">
              <span className="shrink-0 h-9 w-9 rounded-xl bg-black/[0.05] grid place-items-center text-ink-muted"><Icon name={g.icon} size={18} /></span>
              <div><div className="font-semibold text-[15px]">{g.title}</div><p className="text-sm text-ink-muted leading-snug mt-0.5">{g.body}</p></div>
            </div>
          ))}
        </div>

        <div className="mt-7 flex justify-end">
          <button onClick={onClose} className="rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white hover:bg-black">Got it</button>
        </div>
      </div>
    </div>
  );
}
